
import React, { useEffect, useRef, useState } from "react";
import EditHeader from "../EditHeader/EditHeader";
import SectionHeader from "../SectionHeader/SectionHeader";
import styles from "./EditLanguage.module.css";
import SelectLauguage from "./Step1/Step1";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { useLocation, useNavigate } from "react-router-dom";
import PopUp from "../Popup/Popup";
import { getUserAgentMergedDataForAgentUpdate, updateAgentFromBackend } from "../../Store/apiStore";
import api, { API_BASE_URL } from "../../Store/apiStore";
import { isPythonBackend, authHeaders } from "../../utils/apiBackend";
import Loader2 from "../Loader2/Loader2";

function EditLanguage() {
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [Loading, setLoading] = useState(true); // 🆕 Start with true
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // 🆕 Track if data loadeddd

  const navigate = useNavigate();
  const location = useLocation();

  const { agentId: stateAgentId, businessId: stateBusinessId } = location.state || {};
  // Header ka naam. sessionStorage me tabhi hota hai jab user Edit Agent flow
  // se aaya ho; direct / refresh pe khaali tha, isliye neeche API se bhi bharte hain.
  const [agentnm, setAgentnm] = useState(sessionStorage.getItem("agentName") || "");

  // Initial baseline
  const initialRef = useRef({ lang: null, code: null });

  // 🆕 Initialize from session storage immediately
  const [currentLang, setCurrentLang] = useState(() => {
    const stored = sessionStorage.getItem("agentLanguage");
    // console.log("🔍 Initial lang from session:", stored);
    return stored || "";
  });

  const [currentLangCode, setCurrentLangCode] = useState(() => {
    const stored = sessionStorage.getItem("agentLanguageCode");
    // console.log("🔍 Initial code from session:", stored);
    return stored || "";
  });

  // console.log("📍 Current state:", { currentLang, currentLangCode });

  // 🆕 Fetch agent data
  useEffect(() => {
    const getAgentIds = () => ({
      finalAgentId:
        stateAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        sessionStorage.getItem("agent_id"),
      finalBusinessId:
        stateBusinessId ||
        sessionStorage.getItem("SelectAgentBusinessId") ||
        sessionStorage.getItem("businessId") ||
        sessionStorage.getItem("bId"),
    });

    const saveAgentName = (agent) => {
      if (!agent?.agentName) return;
      sessionStorage.setItem("agentName", agent.agentName);
      setAgentnm(agent.agentName);
    };

    const fetchAgentData = async () => {
      const ssLang = sessionStorage.getItem("agentLanguage");
      const ssCode = sessionStorage.getItem("agentLanguageCode");

      // console.log("📦 Session storage check:", { ssLang, ssCode });

      // If session data exists, use it
      if (ssLang && ssCode) {
        // console.log("✅ Using session storage data");

        if (initialRef.current.lang === null && initialRef.current.code === null) {
          initialRef.current = { lang: ssLang, code: ssCode };
        }

        setCurrentLang(ssLang);
        setCurrentLangCode(ssCode);
        setIsDataLoaded(true);
        setLoading(false);

        // Language session se mil gayi, par header ka naam nahi: sirf naam ke
        // liye background me fetch; page ka load isse nahi rukta.
        if (!sessionStorage.getItem("agentName")) {
          const { finalAgentId, finalBusinessId } = getAgentIds();
          if (finalAgentId && finalBusinessId) {
            getUserAgentMergedDataForAgentUpdate(finalAgentId, finalBusinessId)
              .then((res) => saveAgentName(res?.data?.agent))
              .catch((err) => console.error("EditLanguage agent name fetch error:", err));
          }
        }
        return;
      }

      // Get agent IDs
      const { finalAgentId, finalBusinessId } = getAgentIds();

      // console.log("🔑 Agent IDs:", { finalAgentId, finalBusinessId });

      // If no IDs, redirect
      if (!finalAgentId || !finalBusinessId) {
        // console.warn("⚠️ No agent IDs found - redirecting");
        navigate("/edit-agent-new");
        return;
      }

      // Fetch from API
      try {
        setLoading(true);
        // console.log("🌐 Fetching agent data from API...");

        const res = await getUserAgentMergedDataForAgentUpdate(
          finalAgentId,
          finalBusinessId
        );

        const agent = res?.data?.agent;

        if (!agent) {
          throw new Error("No agent data received");
        }

        saveAgentName(agent);

        const newLang = agent?.agentLanguage || "English"; // 🆕 Default fallback
        const newCode = agent?.agentLanguageCode || "en-US";

        // console.log("✅ Fetched language data:", { newLang, newCode });

        // Save to session
        sessionStorage.setItem("agentLanguage", newLang);
        sessionStorage.setItem("agentLanguageCode", newCode);

        // Update state
        setCurrentLang(newLang);
        setCurrentLangCode(newCode);

        // Set baseline
        initialRef.current = { lang: newLang, code: newCode };
        setIsFormChanged(false);
        setIsDataLoaded(true);

        // console.log("💾 Data loaded and saved");

      } catch (err) {
        // console.error("❌ EditLanguage fetch error:", err);
        setPopupType("error");
        setPopupMessage("Failed to load agent data. Please try again.");
        setShowPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [stateAgentId, stateBusinessId, navigate]);

  const handleStepChange = ({ lang, code }) => {
    // console.log("📝 Language changed:", { lang, code });

    setCurrentLang(lang);
    setCurrentLangCode(code);

    const init = initialRef.current;
    const changed = lang !== init.lang || code !== init.code;
    setIsFormChanged(changed);

    // console.log("🔄 Form changed:", changed);

    sessionStorage.setItem("agentLanguage", lang);
    sessionStorage.setItem("agentLanguageCode", code);
  };

  const handleSave = async () => {
    if (!isFormChanged) {
      // console.log("⚠️ No changes to save");
      // return;
      setTimeout(() => {
        navigate("/edit-gender", {
          state: {
            agentId: stateAgentId,
            businessId: stateBusinessId,
          },
        });
      }, 2000);
    }

    try {
      setLoading(true);

      const payload = {
        agent_id: stateAgentId,
        agentLanguage: currentLang,
        agentLanguageCode: currentLangCode,
      };

      // console.log("💾 Saving payload:", payload);

      // Python agent ka Retell me koi LLM/agent hota hi nahi, is liye Node ka
      // /agentV2/updateAgent client.agent.retrieve(agent_id) pe 500 de deta hai
      // — aur DB write us call ke NEECHE hai (step 7), to language kabhi save
      // hi nahi hoti. updateAgentBusinessPython wahi DB field-mapping karta hai
      // par Retell ko bilkul chhuta nahi (EditNameAvtar bhi yahi use karta hai).
      //
      // Column save karna kaafi NAHI hai. Language prompt ke ANDAR baked hoti
      // hai ("You are jassi, a male receptionist fluent in Russian...", aur
      // "must respect Russian and converse only in that language"). Sirf column
      // badla to dashboard Hindi dikhata hai par agent purani language me hi
      // baat karta hai — prompt ki 20,000 characters usi ko dohraati rehti hain.
      //
      // Is liye save ke baad Python ka /edit chalta hai, jo rawPromptTemplate ko
      // nayi language ke saath dobara render karta hai. Ye gender/voice/avatar
      // se alag hai — wo columns hain, seedha padhe jaate hain.
      let res;
      if (isPythonBackend()) {
        const nodeRes = await api.patch(
          `${API_BASE_URL}/agentV2/updateAgentBusinessPython`,
          payload
        );
        res = nodeRes?.data;

        // Best-effort: prompt regen fail ho to bhi language save ho chuki hai.
        // Chup-chaap nigalne ke bajaye log karo — warna "save to ho gaya par
        // agent nahi badla" wapas debug karna padega.
        try {
          const r = await fetch(
            `${process.env.REACT_APP_PY_API_BASE_URL}/agents/${stateAgentId}/edit`,
            { method: "POST", headers: authHeaders() }
          );
          if (!r.ok) {
            console.error("Prompt regen failed:", r.status, await r.text());
          }
        } catch (e) {
          console.error("Prompt regen could not run:", e);
        }
      } else {
        res = await updateAgentFromBackend(payload);
      }

      // console.log("✅ Update response:", res);

      if (res?.success) {
        setPopupType("success");
        setPopupMessage("Language updated successfully!");
        setShowPopup(true);
        setTimeout(() => {
          navigate("/edit-gender", {
            state: {
              agentId: stateAgentId,
              businessId: stateBusinessId,
            },
          });
        }, 2000);

        // Update baseline after successful save
        initialRef.current = { lang: currentLang, code: currentLangCode };
        setIsFormChanged(false);

        sessionStorage.setItem("nextEditRoute", "/edit-gender");
      } else {
        throw new Error(res?.message || "Update failed");
      }
    } catch (error) {
      // console.error("❌ Save error:", error);
      setPopupType("error");
      setPopupMessage("Failed to update language. Please try again.");
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Show loading state
  if (Loading || !isDataLoaded) {
    return (
      <div>
        <EditHeader title="Edit Agent" agentName={agentnm} />
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Loader2 />
        </div>
      </div>
    );
  }

  return (
    <div>
      <EditHeader title="Edit Agent" agentName={agentnm} />

      <div className={styles.Maindiv}>
        <SectionHeader
          heading="Select Language"
          subheading="Select the Primary Language, You want your agent to speak"
          highlight=""
        />
      </div>

      <div className={styles.container}>
        {/* 🆕 Only render after data loaded */}
        {isDataLoaded && (
          <SelectLauguage
            key={`${currentLang}-${currentLangCode}`} // 🆕 Force re-render on change
            defaultLang={currentLang}
            defaultCode={currentLangCode}
            onStepChange={handleStepChange}
          />
        )}
      </div>

      {/* Save button ke peeche white bar — neeche ke language cards button ke
          through na dikhein. */}
      <div className={styles.saveBar} />

      <div className={styles.stickyWrapper}>
        <AnimatedButton
          label="Save"
          isLoading={Loading}
          // disabled={!isFormChanged}
          onClick={handleSave}
        />
      </div>

      {showPopup && (
        <PopUp
          type={popupType}
          onClose={() => setShowPopup(false)}
          message={popupMessage}
          onConfirm={() => {
            setShowPopup(false);
            if (popupType === "success") {
              navigate("/edit-gender");
            }
          }}
        />
      )}
    </div>
  );
}

export default EditLanguage;